import { useState } from 'react';

import { ContextConfigContent } from 'utils/gloo-types';

import Button from 'components/atoms/Button';
import Checkbox from 'components/atoms/Checkbox';
import HelpPopover from 'components/molecules/HelpPopover';
import Input from 'components/molecules/Input';
import Typography from 'components/atoms/Typography';

interface ContentMetricsProps {
  content: ContextConfigContent;
  onUpdateContent: (content: ContextConfigContent) => void;
}

type Metric = ContextConfigContent['metrics'][0];
const defaultMetric: Metric = { id: 0, type: 'numeric', label: '' };

function SurveyMetrics({ content, onUpdateContent }: ContentMetricsProps) {
  const metrics = content?.metrics || [];

  const [deletedMetrics, setDeletedMetrics] = useState<Metric[]>([]);

  const updateMetric = (id: number, data: Partial<Metric>) => {
    return content?.metrics?.map((em) => (em.id === id ? { ...em, ...data } : em));
  };

  const addNewMetric = () => {
    const newMetrics = [...metrics];
    let newMetricId = (newMetrics[newMetrics.length - 1]?.id || 0) + 1;
    if (deletedMetrics.some((dm) => dm.id === newMetricId)) {
      newMetricId += 1;
    }
    newMetrics.push({ ...defaultMetric, id: newMetricId });
    return newMetrics;
  };

  return (
    <div className="mt-5">
      <div className="pl-1 flex justify-between">
        <div className="flex items-center">
          <Typography className="text-blackDark" variant="label">
            What metrics do you want to capture?
          </Typography>
          <HelpPopover
            inline
            className="ml-1"
            size={14}
            content="These are the metrics that Gloo will automatically group together and give you an easy way to access responses."
          />
        </div>
        <div className="flex items-center">
          <Typography className="text-blackDark" variant="label">
            Numeric?
          </Typography>
          <HelpPopover
            inline
            className="ml-1"
            size={14}
            content="When this is checked, we will require the person to answer with a number."
          />
        </div>
      </div>
      <div className="flex flex-col justify-center">
        {metrics.map((m) => (
          <div key={`${m.id}`} className="flex flex-row items-center my-2">
            <Input
              placeholder="Metric Name"
              onBlur={(e) =>
                onUpdateContent({ ...content, metrics: updateMetric(m.id, { label: e?.target?.value || '' }) })
              }
              defaultValue={m.label}
            />
            <div className="mx-1">
              <Checkbox
                checked={m.type === 'numeric'}
                onChange={() =>
                  onUpdateContent({
                    ...content,
                    metrics: updateMetric(m.id, { type: m.type === 'numeric' ? 'subjective' : 'numeric' }),
                  })
                }
              />
            </div>
            <Button
              onClick={() => {
                setDeletedMetrics((dms) => [...dms, m]);
                onUpdateContent({
                  ...content,
                  metrics: content?.metrics?.filter((em) => em.id !== m.id),
                  // Remove any question metricIds that had this metric attached
                  questions: (content?.questions || []).map((eq) =>
                    eq.metricId === m.id ? { ...eq, metricId: 0 } : eq,
                  ),
                });
              }}
              type="transparent"
              dataDurableId="updateMetricsButton"
              icon="trash-2"
              iconSize={14}
              className="ml-4 text-red"
            />
          </div>
        ))}
        {deletedMetrics.length > 0 && (
          <div className="flex flex-row text-red items-center text-sm mt-1">
            If you delete a metric, you will lose any data associated with it.
            <Button
              onClick={() =>
                onUpdateContent({ ...content, metrics: [...metrics, deletedMetrics.pop()].filter(Boolean) })
              }
              text="Undo Delete"
              dataDurableId="undoDeleteButton"
              type="transparent"
              icon="rotate-ccw"
              iconSize={14}
              className="ml-4 text-red"
            />
          </div>
        )}
        <div>
          <Button
            onClick={() => onUpdateContent({ ...content, metrics: addNewMetric() })}
            text="Add New Metric"
            type="secondary"
            dataDurableId="addNewMetricButton"
            icon="plus"
            size="small"
            className="mt-2"
            data-analytics="survey-add-new-metric"
          />
        </div>
      </div>
    </div>
  );
}

export default SurveyMetrics;
